import { format, formatDistance, isToday } from 'date-fns';

type DevLogOptions = { memoize?: boolean };
type DevLogArgs = [...Parameters<typeof console.log>] | [...Parameters<typeof console.log>, DevLogOptions];

const memoizedLogs = new Map<string, string>();

/**
 * Logs to console only when localStorage key "lspro::debug" is set to "true".
 * When `memoize` is true, skips logging if arguments for the same label haven't changed.
 * If the first argument is a string starting with "⚠️", logs as a warning instead of a standard log.
 */
export function devLog(...args: DevLogArgs) {
  if (typeof window === 'undefined' || !window.localStorage || window.localStorage.getItem('lspro::debug') !== 'true') {
    return;
  }

  const maybeOptions = args[args.length - 1];
  const hasOptions = Boolean(maybeOptions && typeof maybeOptions === 'object' && 'memoize' in maybeOptions);
  const logArgs = hasOptions ? (args.slice(0, -1) as Parameters<typeof console.log>) : args;
  const options = hasOptions ? (maybeOptions as DevLogOptions) : undefined;

  if (options?.memoize) {
    const memoKey = typeof logArgs[0] === 'string' ? logArgs[0] : '__lspro::debug__';

    try {
      const serialized = JSON.stringify(logArgs);

      if (serialized === memoizedLogs.get(memoKey)) return;

      memoizedLogs.set(memoKey, serialized);
    } catch {
      // If serialization fails, fall back to logging without memoization
    }
  }

  if (typeof logArgs[0] === 'string' && logArgs[0].startsWith('⚠️')) {
    console.warn('⚙️', ...logArgs);
  } else {
    console.log('⚙️', ...logArgs);
  }
}

export function expiresAt(millisFromNow: number) {
  if (isNaN(millisFromNow)) return '- invalid expiration';
  if (millisFromNow <= 0) return '- already expired';

  const date = Date.now() + millisFromNow;
  const today = isToday(date);

  return `${format(date, today ? 'HH:mm:ss' : 'dd MMMM HH:mm:ss')} (in ${formatDistance(0, millisFromNow)})`;
}
